import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useEmpresaAtiva } from "../hooks/useEmpresaAtiva";
import { apiFetch } from "../lib/api";
import { useWhatsappNaoLidas } from "../hooks/useWhatsapp";
import { Bell, Mail, MessageCircle } from "lucide-react";

import { EsqueletoLista } from "./pele/Esqueleto"; /* direto do arquivo, não do barril: o barril puxava a pele inteira pro caminho crítico */
import { diaCivilDe, diaCurto } from "../lib/format";
/**
 * Sino de notificações — feed das ações recentes dos OUTROS usuários (sócios),
 * lido do audit_log via /api/auditoria/notificacoes. O "lido/não-lido" mora no
 * localStorage (por dispositivo): o contador compara o `quando` de cada item com
 * a última vez que o painel foi aberto. Atualiza ao abrir + a cada 30s.
 * Só admin/sócio vê (rota é admin-only; aqui escondemos o ícone pros demais).
 */

interface Notif {
  id: string;
  quem: string;
  texto: string;
  rota: string;
  quando: string; // ISO
}

const VISTO_KEY = "notif-visto-em";

function tempoRelativo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias === 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;
  return diaCurto(diaCivilDe(iso));
}

export function SinoNotificacoes() {
  const { user, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [vistoEm, setVistoEm] = useState<string>(
    () => localStorage.getItem(VISTO_KEY) || new Date(0).toISOString()
  );
  const ref = useRef<HTMLDivElement>(null);

  const ehAdmin = user?.role === "admin";
  // Cross-tenant (P0): o sino também disparava sem esperar a empresa ativa e com queryKey
  // sem empresaId — dava pra piscar notificação/contador de outro tenant na janela do header.
  const { ativa: empresaAtiva } = useEmpresaAtiva();
  const prontoEmpresa = ehAdmin && !!empresaAtiva?.id;

  const lista = useQuery({
    queryKey: ["notificacoes", empresaAtiva?.id],
    queryFn: () => apiFetch<{ data: Notif[] }>("/auditoria/notificacoes", getAccessToken()),
    enabled: prontoEmpresa,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const itens = lista.data?.data ?? [];
  const naoLidos = itens.filter((n) => new Date(n.quando) > new Date(vistoEm)).length;

  // E-mails não lidos do Zoho (escopo da empresa ativa). Soma no contador do sino.
  const emails = useQuery({
    queryKey: ["zoho-mail-unread", empresaAtiva?.id],
    queryFn: () => apiFetch<{ total: number }>("/zoho/mail/unread", getAccessToken()),
    enabled: prontoEmpresa,
    staleTime: 60000,
    refetchInterval: 120000,
    refetchOnWindowFocus: true,
  });
  const emailsNaoLidos = emails.data?.total ?? 0;

  // WhatsApp não lido (UAZAPI). Soma no contador do sino.
  //
  // 🟥 14/08 — ERA AQUI QUE VAZAVA. Este bloco pedia `/whatsapp/nao-lidas` com
  // `enabled: prontoEmpresa` (= é admin + tem empresa), sem perguntar ao servidor
  // se a pessoa pode abrir as conversas. Quem é admin e está FORA da allowlist
  // (`site_config.whatsapp_acesso`) levava 403 a cada carregamento do painel —
  // 192 negativas medidas entre 13/07 e 14/08, todas de um usuário só.
  //
  // O #1560 trancou o Layout e o número NÃO caiu, porque as duas consultas
  // compartilham a queryKey `wa-nao-lidas`: no React Query isso é UMA consulta
  // com dois observadores, e basta um habilitado pra ela disparar. Agora a
  // permissão vem da régua única de `hooks/useWhatsapp`.
  const { total: whatsNaoLidos } = useWhatsappNaoLidas();

  const totalBadge = naoLidos + emailsNaoLidos + whatsNaoLidos;

  // Ao abrir, marca tudo como lido (zera o contador).
  function abrir() {
    setAberto((v) => {
      const novo = !v;
      if (novo) {
        const agora = new Date().toISOString();
        localStorage.setItem(VISTO_KEY, agora);
        setVistoEm(agora);
      }
      return novo;
    });
  }

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!aberto) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [aberto]);

  if (!ehAdmin) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={abrir}
        className="relative p-2 rounded-md hover:bg-muted text-ink-soft hover:text-foreground transition-colors"
        title="Notificações"
        aria-label={`Notificações${totalBadge > 0 ? ` (${totalBadge} não lidas)` : ""}`}
      >
        <Bell className="h-5 w-5" />
        {totalBadge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center tabular-nums">
            {totalBadge > 9 ? "9+" : totalBadge}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-card border rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-2.5 border-b flex items-center justify-between">
            <span className="text-sm font-semibold">Atividade recente</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">dos sócios</span>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {whatsNaoLidos > 0 && (
              <button
                onClick={() => {
                  navigate("/whatsapp");
                  setAberto(false);
                }}
                className="w-full text-left px-4 py-2.5 border-b hover:bg-muted/60 transition-colors flex gap-2.5 items-center bg-emerald-50/60"
              >
                <MessageCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="text-[13px] leading-snug block font-medium">
                    {whatsNaoLidos} mensagem{whatsNaoLidos > 1 ? "s" : ""} de WhatsApp não lida{whatsNaoLidos > 1 ? "s" : ""}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">Abrir WhatsApp →</span>
                </span>
              </button>
            )}
            {emailsNaoLidos > 0 && (
              <button
                onClick={() => {
                  navigate("/comunicacao");
                  setAberto(false);
                }}
                className="w-full text-left px-4 py-2.5 border-b hover:bg-muted/60 transition-colors flex gap-2.5 items-center bg-amber-50/60"
              >
                <Mail className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="text-[13px] leading-snug block font-medium">
                    {emailsNaoLidos} e-mail{emailsNaoLidos > 1 ? "s" : ""} não lido{emailsNaoLidos > 1 ? "s" : ""}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">Abrir caixa de entrada →</span>
                </span>
              </button>
            )}
            {lista.isLoading && (
              <EsqueletoLista className="px-4 py-3" linhas={3} alturaLinha="h-10" rotulo="Carregando os avisos…" />
            )}
            {!lista.isLoading && itens.length === 0 && emailsNaoLidos === 0 && whatsNaoLidos === 0 && (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                Nada novo por aqui. ✨
              </div>
            )}
            {itens.map((n) => {
              const novo = new Date(n.quando) > new Date(vistoEm);
              return (
                <button
                  key={n.id}
                  // 🚪 Item SEM rota não é clicável — e não finge ser. (auditoria de Sistema,
                  // 14/07: o aviso de teto de IA ia pros 4 admins e o clique levava a
                  // "Acesso restrito". Aviso que bate numa parede ensina a ignorar avisos.)
                  disabled={!n.rota}
                  onClick={() => {
                    if (!n.rota) return;
                    navigate(n.rota);
                    setAberto(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 border-b last:border-0 transition-colors flex gap-2.5 items-start ${
                    n.rota ? "hover:bg-muted/60" : "cursor-default"
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${novo ? "bg-rose-500" : "bg-transparent"}`}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="text-[13px] leading-snug block">
                      <span className="font-semibold">{n.quem}</span> {n.texto}
                    </span>
                    <span className="text-[12.5px] text-muted-foreground">{tempoRelativo(n.quando)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
